import { useEffect } from 'react';

import { Checkbox } from '../../form';
import { Input } from '../../form';

import { useTranslation } from 'react-i18next';
import { useTargets } from '../../../hooks/useTargets';
import { AssetTarget } from '../../NewStrategy/AssetTarget';
import { formatAWSDate } from '../../../helpers/functions';
import InfoTooltip from '../../InfoTooltip/Info-tooltip';
import { AssetAdvancedSettingsProps } from './types';
import { logger } from '../../../helpers/logger';

export const AssetAdvancedSettings = ({
  className,
  assetCurrency,
  reportingCurrency,
  entryExchangePrice,
  formik,
  onlyTargets = false,
}: AssetAdvancedSettingsProps) => {
  const { t } = useTranslation();

  const entryPrice = formik.values.entryPriceInAssetCurrency ?? 0;

  const { targets, removeTarget, updateTargets } = useTargets({
    alert: formik.values.priceAlert!,
    entryPrice,
    inspirationSource: formik.values.inspirationSource ?? '',
    items: formik.values.stopProfitAndLossEntries,
    entryExchangePrice: formik.values.entryExchangePrice,
  });

  useEffect(() => {
    logger.log(
      'AssetAdvancedSettings:useEffect',
      targets,
      formik.values.stopProfitAndLossEntries
    );

    formik.setValues({
      ...formik.values,
      stopProfitAndLossEntries: targets.map((t) => ({
        alert: t.alert,
        endDate: t.endDate ? formatAWSDate(t.endDate) : undefined,

        stopLoss: t.stopLoss.AMOUNT ?? undefined,
        stopLossPercentage: t.stopLoss.PERCENTAGE ?? undefined,
        stopLossInAssetCurrency: t.stopLoss.AMOUNT ?? undefined,
        stopLossInReportingCurrency:
          t.stopLoss.AMOUNT_IN_REPORTING_CURRENCY ?? undefined,

        stopProfit: t.stopProfit.AMOUNT ?? undefined,
        stopProfitPercentage: t.stopProfit.PERCENTAGE ?? undefined,
        stopProfitInAssetCurrency: t.stopProfit.AMOUNT ?? undefined,
        stopProfitInReportingCurrency:
          t.stopProfit.AMOUNT_IN_REPORTING_CURRENCY ?? undefined,
      })),
    });
  }, [targets]);

  return (
    <div
      className={`
        flex
        flex-col
        w-full
        ${className || ''}
      `}
    >
      <div className="flex justify-between items-center">
        <div className={'flex items-center'}>
          <span className="font-medium">
            {t('strategies.assetAdvancedSettings.addStopProfitLoss')}
          </span>
          <InfoTooltip
            placement="right"
            content={t(
              'strategies.assetAdvancedSettings.addStopProfitLossTooltip'
            )}
            isTooltipIconVisible
            tooltipIconClassName={'ml-1'}
            tooltipPopupClassname={'flex w-96'}
          />
        </div>
        <div className={'flex items-end'}>
          <span
            className={`
            text-xs
            font-medium
            cursor-pointer
            text-grey-50
          `}
            onClick={() => removeTarget(0)}
          >
            {t('strategies.assetAdvancedSettings.clearAll')}
          </span>
        </div>
      </div>

      {targets.map((target, targetIndex) => (
        <AssetTarget
          key={'target_' + targetIndex}
          assetCurrency={assetCurrency}
          reportingCurrency={reportingCurrency}
          entryPrice={entryPrice}
          purchasedAt={formik.values.purchasedAt ?? formatAWSDate(new Date())}
          removeTarget={removeTarget}
          targetIndex={targetIndex}
          target={target}
          updateAsset={updateTargets}
          entryExchangePrice={entryExchangePrice}
          canRemove={false}
        />
      ))}
      {/*  <div className="flex items-center my-5">
        <Icon
          svgName="plusButton"
          className={`
            ${
              canAddNewTarget
                ? 'cursor-pointer'
                : 'pointer-events-none cursor-not-allowed text-grey-30'
            }
          `}
          onClick={addTarget}
        />
        <span className="text-xs ml-1.5">Add stop profit and Loss</span>
      </div> */}
      <div className="flex items-center">
        <Checkbox
          formik={formik}
          id="priceAlert"
          isCheck={formik.values.priceAlert}
          key="priceAlert"
          name="priceAlert"
          shape={'square'}
        >
          <label htmlFor="priceAlert" className="text-sm">
            {t('strategies.assetAdvancedSettings.form.label.priceAlert')}
          </label>
        </Checkbox>
      </div>

      {!onlyTargets && (
        <div className={'w-full pb-4'}>
          <div className="flex items-center mt-16 font-medium text-sm">
            <span>{t('strategies.assetAdvancedSettings.custodian')}</span>
            <InfoTooltip
              placement="right"
              content={t('strategies.assetAdvancedSettings.custodianTooltip')}
              isTooltipIconVisible
              tooltipIconClassName={'ml-1'}
              tooltipPopupClassname={'flex w-96'}
            />
          </div>

          <div className={'w-full mt-8 '}>
            <Input
              key="bank"
              size="full"
              name="bank"
              id="bank"
              formik={formik}
              type="text"
              placeholder={t(
                'strategies.configureAsset.form.placeholder.custodian'
              )}
              label={t('strategies.configureAsset.form.label.custodian')}
            />
          </div>
        </div>
      )}

      {!onlyTargets && (
        <div className={'w-full pb-4'}>
          <div className={'flex items-center mt-8 font-medium text-sm'}>
            <span>
              {t('strategies.assetAdvancedSettings.addInspirationSource')}
            </span>
            <InfoTooltip
              placement="right"
              content={t(
                'strategies.assetAdvancedSettings.inspirationSourceTooltip'
              )}
              isTooltipIconVisible
              tooltipIconClassName={'ml-1'}
              tooltipPopupClassname={'flex w-96'}
            />
          </div>
          <div className={'w-full mt-8 '}>
            <Input
              key="inspirationSource"
              size="full"
              name="inspirationSource"
              id="inspirationSource"
              formik={formik}
              type="text"
              placeholder={t(
                'strategies.assetAdvancedSettings.form.placeholder.inspirationSource'
              )}
              label={t(
                'strategies.assetAdvancedSettings.form.label.inspirationSource'
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};
